/**
 * Internal promotions tab — discounts the admin can pre-define once
 * (Flyer -10€, Promoción mayo 50%, Cortesía, etc.) and reuse from
 * walk-in / calendar manual bookings. These are NEVER shown on the
 * public web — only inside the admin panel and on invoices.
 */
import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Tag, Plus, Edit2, Trash2, RefreshCw } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
} from '../ui/dialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TYPE_LABEL = {
  fixed: 'Importe fijo (€)',
  percent: 'Porcentaje (%)',
  final_price: 'Precio final (€)',
};

const empty = {
  label: '',
  discount_type: 'fixed',
  discount_value: 10,
  notes: '',
  active: true,
  code: '',
  valid_until: '',
};

export const InternalPromosTab = ({ authHeader, userRole }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/internal-promos`, {
        headers: { Authorization: authHeader },
      });
      setItems(Array.isArray(r.data) ? r.data : []);
    } catch {
      toast.error('Error al cargar promos internas');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (authHeader) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authHeader]);

  const save = async (data) => {
    try {
      if (data.id) {
        await axios.patch(`${API}/admin/internal-promos/${data.id}`, data, {
          headers: { Authorization: authHeader },
        });
      } else {
        await axios.post(`${API}/admin/internal-promos`, data, {
          headers: { Authorization: authHeader },
        });
      }
      toast.success('Guardado');
      setEditing(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al guardar');
    }
  };

  const remove = async (p) => {
    if (!window.confirm(`Eliminar la promo "${p.label}"?`)) return;
    try {
      await axios.delete(`${API}/admin/internal-promos/${p.id}`, {
        headers: { Authorization: authHeader },
      });
      toast.success('Promo eliminada');
      load();
    } catch {
      toast.error('Error al eliminar');
    }
  };

  return (
    <div className="space-y-6" data-testid="internal-promos-tab">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <div>
          <h2 className="text-2xl font-heading text-halo-charcoal flex items-center gap-2">
            <Tag size={22} /> Promos internas
          </h2>
          <p className="text-sm text-halo-text">
            Descuentos para reservas manuales (no aparecen en la web). Ej: &quot;Flyer -10€&quot;,
            &quot;Promoción mayo 50%&quot;, &quot;Cortesía&quot;.
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={load} disabled={loading}>
            <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
          </Button>
          <Button
            onClick={() => setEditing(empty)}
            className="bg-halo-brown hover:bg-halo-charcoal text-halo-cream"
            data-testid="new-internal-promo"
          >
            <Plus size={16} className="mr-2" /> Nueva
          </Button>
        </div>
      </div>

      <div className="grid gap-3">
        {items.length === 0 && (
          <div className="bg-white border border-halo-taupe/15 rounded-xl p-8 text-center text-sm text-halo-text">
            Aún no hay promos internas. Crea una con el botón &quot;Nueva&quot;.
          </div>
        )}
        {items.map((p) => (
          <div
            key={p.id}
            className="bg-white border border-halo-taupe/15 rounded-xl p-4 flex flex-wrap items-center justify-between gap-3"
            data-testid={`promo-row-${p.id}`}
          >
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-0.5 flex-wrap">
                <p className="font-medium text-halo-charcoal">{p.label}</p>
                {p.code && (
                  <span
                    className="text-xs font-mono px-1.5 py-0.5 rounded bg-halo-brown/10 text-halo-brown font-semibold tracking-wider"
                    data-testid={`promo-code-${p.id}`}
                  >
                    {p.code}
                  </span>
                )}
                {p.expired && (
                  <span className="text-xs px-1.5 py-0.5 rounded bg-red-100 text-red-700 font-semibold">
                    Caducada
                  </span>
                )}
                {!p.active && (
                  <span className="text-xs px-1.5 py-0.5 rounded bg-gray-100 text-gray-600">
                    Inactiva
                  </span>
                )}
              </div>
              <p className="text-xs text-halo-text">
                {TYPE_LABEL[p.discount_type] || p.discount_type} ·
                {' '}<strong>{p.discount_value}{p.discount_type === 'percent' ? '%' : '€'}</strong>
                {p.valid_until ? ` · válida hasta ${p.valid_until}` : ''}
                {p.notes ? ` · ${p.notes}` : ''}
              </p>
            </div>
            <div className="flex gap-2">
              <Button size="sm" variant="outline" onClick={() => setEditing(p)}>
                <Edit2 size={14} />
              </Button>
              {(userRole === 'admin' || userRole === 'office') && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => remove(p)}
                  className="text-red-500 border-red-200 hover:bg-red-50"
                  data-testid={`delete-promo-${p.id}`}
                >
                  <Trash2 size={14} />
                </Button>
              )}
            </div>
          </div>
        ))}
      </div>

      <PromoDialog
        open={!!editing}
        promo={editing}
        onClose={() => setEditing(null)}
        onSave={save}
      />
    </div>
  );
};

const PromoDialog = ({ open, promo, onClose, onSave }) => {
  const [data, setData] = useState(empty);

  useEffect(() => {
    if (promo) setData({ ...empty, ...promo });
  }, [promo]);

  if (!promo) return null;
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md" data-testid="promo-dialog">
        <DialogHeader>
          <DialogTitle>{data.id ? 'Editar promo' : 'Nueva promo'}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3 mt-2">
          <div>
            <Label>Nombre *</Label>
            <Input
              value={data.label}
              onChange={(e) => setData({ ...data, label: e.target.value })}
              placeholder="Flyer -10€"
              data-testid="promo-label"
            />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label>Tipo</Label>
              <select
                className="w-full mt-1 rounded-md border border-halo-taupe/20 px-2 py-2 bg-white text-sm"
                value={data.discount_type}
                onChange={(e) => setData({ ...data, discount_type: e.target.value })}
                data-testid="promo-type"
              >
                <option value="fixed">Importe fijo (€)</option>
                <option value="percent">Porcentaje (%)</option>
                <option value="final_price">Precio final (€)</option>
              </select>
            </div>
            <div>
              <Label>Valor</Label>
              <Input
                type="number"
                step="0.01"
                value={data.discount_value}
                onChange={(e) => setData({ ...data, discount_value: parseFloat(e.target.value) || 0 })}
                data-testid="promo-value"
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label>
                Código público{' '}
                <span className="text-xs font-normal text-halo-text">(opcional)</span>
              </Label>
              <Input
                value={data.code || ''}
                onChange={(e) =>
                  setData({ ...data, code: e.target.value.toUpperCase() })
                }
                placeholder="VANDALAS"
                className="font-mono uppercase tracking-wider"
                data-testid="promo-code"
              />
            </div>
            <div>
              <Label>
                Válida hasta{' '}
                <span className="text-xs font-normal text-halo-text">(opcional)</span>
              </Label>
              <Input
                type="date"
                value={data.valid_until || ''}
                onChange={(e) => setData({ ...data, valid_until: e.target.value })}
                data-testid="promo-valid-until"
              />
            </div>
          </div>
          <div>
            <Label>Notas internas</Label>
            <Textarea
              rows={2}
              value={data.notes || ''}
              onChange={(e) => setData({ ...data, notes: e.target.value })}
              placeholder="Para qué se usa..."
            />
          </div>
          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <input
              type="checkbox"
              checked={data.active}
              onChange={(e) => setData({ ...data, active: e.target.checked })}
            />
            Activa (visible al crear reservas)
          </label>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={onClose}>Cancelar</Button>
            <Button
              onClick={() => onSave(data)}
              disabled={!data.label.trim()}
              className="bg-halo-brown hover:bg-halo-charcoal text-halo-cream"
              data-testid="promo-save"
            >
              Guardar
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default InternalPromosTab;
